import { AnimatePresence, motion } from 'framer-motion';
import type { PointerEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { getELCInfosProducts } from '../api/elc/elcProducts';
import squareIcon from '../assets/icons/Square.svg';
import unionIcon from '../assets/icons/Union.svg';
import { Category, CliniqueState, Product } from '../type';
import { BeautyKit2, Effect, Layer, Shapetype } from './BeautyKit2';
import ShadematchLoadingOverlay from './LoadingOverlay';
import { trackPlausibleEvent } from '../services/analytics';
import { isShadematchShadeAvailable } from './shadeAvailability';

type ShadematchShade = {
	name: string;
	color: string;
	skuId?: string;
};

type ShadematchProduct = {
	id: string;
	productCode: string;
	name: string;
	coverageKey: string;
	finishKey: string;
	recommendedShadeName: string;
	shades: ShadematchShade[];
};

type ShadematchApiShade = {
	id: string;
	name: string;
	productSkus: string[];
};

type ShadematchApiRecommendation = {
	product: string;
	productName: string;
	match: ShadematchApiShade | null;
	lighter: ShadematchApiShade | null;
	darker: ShadematchApiShade | null;
};

type ShadematchApiResponse = {
	ita: number;
	undertone: string;
	undertone_confidence: number;
	market: string;
	recommendation: ShadematchApiRecommendation[];
};

type BK2MakeupTemplate = {
	opacity?: number;
	effect?: number;
	effectID?: number;
	shapeType?: number;
	beautifierLevel?: number;
	shineLevel?: number;
	shape:
		| string
		| {
				id: number;
				type: number;
				area: number;
				path: string;
		  };
	name: string;
	sku: string;
	color?: {
		a: number;
		r: number;
		g: number;
		b: number;
	};
};

const shadematchProductBaseIds = [
	{
		prodBaseId: '5276', category: Category.foundation, priority: 0, skuID: '5276', instoreSku: '5276',
		// Loki SKU list for all US Even Better shades (the proxy accepts SKU IDs only).
		variantIds: [
			'6MNY01', '6MNY02', '6MNY03', '6MNY04', '6MNY05', '6MNY06', '6MNY07', '6MNY08', '6MNY09', '6MNY10',
			'6MNY11', '6MNY12', '6MNY13', '6MNY14', '6MNY15', '6MNY16', '6MNY17', '6MNY18', '6MNY19', '6MNY20',
			'6MNY24', '6MNY25', '6MNY26', '6MNY27', '6MNY28', '6MNY29', '6MNY30', '6MNY31', '6MNY32', '6MNY33',
			'6MNY36', '6MNY37', '6MNY38', '6MNY39', '6MNY40', '6MNY41', '6MNY42', '6MNY43', '6MNY44', '6MNY46',
			'6MNY47', '6MNY48', '6MNY49', '6MNY50', '6MNY51', '6MNY52', '6MNY53', '6MNY56', '6MNY59', '6MNY60',
		],
	},
];

const shadematchProductCodesByBaseId: Record<string, string> = {
	'5276': 'EBMU',
	'80532': 'EBSF',
	'9285': 'AS',
	'34817': 'BP',
	'197426': 'EBV',
	'4989': 'SUPER',
	'131489': 'EBV',
};

const shadematchProductDetailsByCode: Record<string, { coverageKey: string; finishKey: string }> = {
	EBM: { coverageKey: 'foundation-moderate', finishKey: 'foundation-natural' },
	EBMU: { coverageKey: 'foundation-moderate', finishKey: 'foundation-natural' },
	EBSF: { coverageKey: 'foundation-full', finishKey: 'foundation-matte' },
	EBV: { coverageKey: 'foundation-light', finishKey: 'foundation-radiant' },
	BP: { coverageKey: 'foundation-full', finishKey: 'foundation-natural-matte' },
	AS: { coverageKey: 'foundation-moderate', finishKey: 'foundation-natural-matte' },
	SUPER: { coverageKey: 'foundation-moderate', finishKey: 'foundation-natural' },
};

const shadematchApiUrl = 'https://demky-clinique-aegneycndwgrbhaf.francecentral-01.azurewebsites.net/api/demky_clinique';
//const shadematchApiUrl = 'http://localhost:7071/api/demky_clinique';
const shadematchProductRequestMarket = 'US';

const productRequests = new Map<string, Promise<ShadematchProduct[]>>();
const shadeRecommendationRequests = new Map<string, Promise<ShadematchApiResponse>>();
let vtoInstanceCounter = 0;

const getRecommendedShadeIndex = (product: ShadematchProduct) => {
	const index = product.shades.findIndex((shade) => shade.name === product.recommendedShadeName);
	return index >= 0 ? index : 0;
};

const formatShadeName = (shadeName: string) => {
	const shadeCode = shadeName.match(/^([A-Z]{1,3})\s*(\d+(?:\.\d+)?)/);
	if (shadeCode) {
		return `${shadeCode[1]} ${shadeCode[2]}`;
	}

	const descriptiveShadeCode = shadeName.trim().match(/^([A-Za-z]+(?:\s+[A-Za-z]+)+)\s+(\d+(?:\.\d+)?)$/);
	if (descriptiveShadeCode) {
		const initials = descriptiveShadeCode[1]
			.split(/\s+/)
			.map((word) => word[0])
			.join('')
			.toUpperCase();

		return `${initials} ${descriptiveShadeCode[2]}`;
	}

	return shadeName;
};

const getShadeNumber = (shadeName: string) => {
	const shadeNumber = shadeName.match(/\d+(?:\.\d+)?/)?.[0];
	return shadeNumber ? Number(shadeNumber) : Number.POSITIVE_INFINITY;
};

const sortShadesByNumber = (shades: ShadematchShade[]) => {
	return [...shades].sort((firstShade, secondShade) => {
		const numberDifference = getShadeNumber(firstShade.name) - getShadeNumber(secondShade.name);
		return numberDifference || firstShade.name.localeCompare(secondShade.name);
	});
};

const getShadeRoleFontSize = (label: string) => {
	if (label.length > 13) return 9;
	if (label.length > 9) return 10;
	return 12;
};

const hexToRgb = (hex: string): [number, number, number] => {
	const normalizedHex = hex.replace('#', '').trim();
	const expandedHex =
		normalizedHex.length === 3
			? normalizedHex
					.split('')
					.map((character) => character + character)
					.join('')
			: normalizedHex;
	const value = Number.parseInt(expandedHex, 16);

	if (Number.isNaN(value)) {
		return [1, 1, 1];
	}

	return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
};

const normalizeShadeId = (shadeId: string) => {
	return shadeId.toUpperCase().replace(/[^A-Z0-9.]/g, '').replace(/0+(\d)/g, '$1');
};

const normalizeRecommendationKey = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

const shadematchProductAliases: Record<string, string> = {
	EBM: 'EBMU',
	EBMU: 'EBMU',
	EVENBETTERMAKEUP: 'EBMU',
	EVENBETTERMAKEUPSPF15: 'EBMU',
	EVENBETTERMAKEUPBROADSPECTRUMSPF15: 'EBMU',
};

const getRecommendationProductKey = (recommendation: ShadematchApiRecommendation) => {
	const normalizedProduct = normalizeRecommendationKey(recommendation.product);
	return shadematchProductAliases[normalizedProduct] ?? normalizedProduct;
};

const getProductRecommendation = (product: ShadematchProduct, recommendation: ShadematchApiResponse) => {
	const productKeys = [product.productCode, product.id, product.name]
		.filter(Boolean)
		.map(normalizeRecommendationKey)
		.map((key) => shadematchProductAliases[key] ?? key);

	const directMatch = recommendation.recommendation.find((item) => {
		const recommendationKey = getRecommendationProductKey(item);
		return productKeys.includes(recommendationKey);
	});

	if (directMatch) {
		return directMatch;
	}

	if (recommendation.recommendation.length === 1) {
		return recommendation.recommendation[0];
	}

	return undefined;
};

const findRecommendedShadeName = (product: ShadematchProduct, recommendation?: ShadematchApiShade | null) => {
	if (!recommendation) {
		return undefined;
	}

	const recommendationProductSkus = recommendation.productSkus.map(normalizeRecommendationKey);
	const productSkuMatch = product.shades.find((shade) => shade.skuId && recommendationProductSkus.includes(normalizeRecommendationKey(shade.skuId)));
	if (productSkuMatch) {
		return productSkuMatch.name;
	}

	const recommendationIds = [recommendation.id, recommendation.name]
		.filter(Boolean)
		.map(normalizeShadeId);

	const shade = product.shades.find((shade) => {
		const normalizedShadeName = normalizeShadeId(shade.name);
		return recommendationIds.some((normalizedRecommendation) =>
			normalizedShadeName === normalizedRecommendation
			|| normalizedShadeName.startsWith(normalizedRecommendation)
			|| normalizedRecommendation.startsWith(normalizedShadeName)
		);
	});

	return shade?.name;
};

const applyShadeRecommendations = (products: ShadematchProduct[], recommendation: ShadematchApiResponse | null) => {
	if (!recommendation) {
		return products;
	}

	return products.map((product) => {
		const productRecommendation = getProductRecommendation(product, recommendation);
		const recommendedShadeName = findRecommendedShadeName(product, productRecommendation?.match);

		if (!recommendedShadeName) {
			return product;
		}

		return {
			...product,
			recommendedShadeName,
		};
	});
};

const imageSourceToDataUrl = async (source: string) => {
	if (source.startsWith('data:')) {
		return source;
	}

	const blob = await fetch(source).then((response) => response.blob());

	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
};

const fetchShadeRecommendation = async (faceImageSource: string, wristImageSource: string, market: string) => {
	const [faceImage, wristImage] = await Promise.all([imageSourceToDataUrl(faceImageSource), imageSourceToDataUrl(wristImageSource)]);

	const response = await fetch(shadematchApiUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			face: faceImage,
			wrist: wristImage,
			market,
		}),
	});

	if (!response.ok) {
		throw new Error(`Shadematch API failed with status ${response.status}`);
	}

	const result: ShadematchApiResponse = await response.json();
	if (!result || !Number.isFinite(result.ita) || !result.undertone
		|| !Array.isArray(result.recommendation) || result.recommendation.length === 0) {
		throw new Error('Shadematch API returned no valid recommendation');
	}
	trackPlausibleEvent('sm_photo_succeeded');
	return result;
};

const mapELCProductToShadematchProduct = (product: Product, market: string): ShadematchProduct | null => {
	const productCode = shadematchProductCodesByBaseId[product.prod_base_id || product.base_id] ?? product.prod_base_id ?? product.base_id;
	const shades = product.items
		?.filter((item) => item.shadeName && item.shadeCode)
		.map((item) => ({
			name: formatShadeName(item.shadeName!),
			color: item.shadeCode!,
			skuId: item.sku_id,
		})) ?? [];
	const availableShades = shades.filter((shade) => isShadematchShadeAvailable(productCode, shade, market));
	const sortedShades = sortShadesByNumber(availableShades);

	if (sortedShades.length === 0) {
		return null;
	}

	const defaultRecommendedShade = sortedShades.find((shade) => shade.name.includes('WN 48'))?.name ?? sortedShades[Math.floor(sortedShades.length / 2)]?.name ?? sortedShades[0].name;
	const productDetails = shadematchProductDetailsByCode[productCode];

	return {
		id: product.prod_base_id || product.base_id,
		productCode,
		name: product.name,
		coverageKey: productDetails?.coverageKey ?? '',
		finishKey: productDetails?.finishKey ?? '',
		recommendedShadeName: defaultRecommendedShade,
		shades: sortedShades,
	};
};

const getShadematchProducts = (market: string) => {
	const cachedRequest = productRequests.get(market);
	if (cachedRequest) {
		return cachedRequest;
	}

	const request = getELCInfosProducts(shadematchProductBaseIds, shadematchProductRequestMarket, { buidLang: 'en' }).then((products) =>
		products
			.sort((firstProduct, secondProduct) => firstProduct.priority - secondProduct.priority)
			.map((product) => mapELCProductToShadematchProduct(product, market))
			.filter((product): product is ShadematchProduct => Boolean(product)),
	);
	productRequests.set(market, request);

	return request;
};

const getShadeRecommendation = (faceImageSource: string, wristImageSource: string, market: string) => {
	const requestKey = JSON.stringify([faceImageSource, wristImageSource, market]);
	const cachedRequest = shadeRecommendationRequests.get(requestKey);
	if (cachedRequest) {
		return cachedRequest;
	}

	const request = fetchShadeRecommendation(faceImageSource, wristImageSource, market);
	shadeRecommendationRequests.set(requestKey, request);

	return request;
};

export const preloadShadematchResults = (faceImageSource: string, wristImageSource: string, market: string) => {
	return Promise.allSettled([getShadematchProducts(market), getShadeRecommendation(faceImageSource, wristImageSource, market)]);
};

const defaultFoundationTemplate: BK2MakeupTemplate = {
	opacity: 180,
	effect: Effect.NORMALBIS,
	shapeType: Shapetype.FOUNDATION,
	beautifierLevel: 0.4,
	shineLevel: 0,
	shape: '/foundation_full.png',
	name: 'Foundation',
	sku: 'foundation',
};

const loadComputedBK2Layers = () =>
	fetch(`${process.env.PUBLIC_URL || ''}/computed-bk2-layers.json`)
		.then((response) => response.json())
		.then((layers: BK2MakeupTemplate[]) => layers);

const getBK2ShapePath = (template: BK2MakeupTemplate) => {
	return typeof template.shape === 'string' ? template.shape : template.shape.path;
};

const getBK2ShapeType = (template: BK2MakeupTemplate) => {
	return typeof template.shape === 'string' ? template.shapeType ?? Shapetype.FOUNDATION : template.shape.type;
};

const getBK2Effect = (template: BK2MakeupTemplate) => {
	return template.effectID ?? template.effect ?? Effect.NORMALBIS;
};

const getBK2Opacity = (template: BK2MakeupTemplate) => {
	return (template.color?.a ?? template.opacity ?? 180) / 255;
};

type ShadematchVirtualTryOnProps = {
	selectedShade: ShadematchShade | null;
	showBeforeAfter: boolean;
	onEnabled: () => void;
	onReady: () => void;
};

const ShadematchVirtualTryOn = ({ selectedShade, showBeforeAfter, onEnabled, onReady }: ShadematchVirtualTryOnProps) => {
	const beautyKit2Ref = useRef<BeautyKit2 | null>(null);
	const instanceIdRef = useRef(`shadematch-vto-${++vtoInstanceCounter}`);
	const [isReady, setIsReady] = useState(false);
	const [hasCameraError, setHasCameraError] = useState(false);
	const [foundationTemplate, setFoundationTemplate] = useState<BK2MakeupTemplate>(defaultFoundationTemplate);
	const videoCanvasId = `${instanceIdRef.current}-camera`;
	const renderingCanvasId = `${instanceIdRef.current}-rendering`;

	useEffect(() => {
		let isMounted = true;

		loadComputedBK2Layers()
			.then((makeupItems: BK2MakeupTemplate[]) => {
				const template = makeupItems.find((item) => item.sku === selectedShade?.skuId) ?? makeupItems.find((item) => getBK2ShapeType(item) === Shapetype.FOUNDATION || getBK2ShapePath(item).includes('foundation'));
				if (isMounted && template) {
					setFoundationTemplate(template);
				}
			})
			.catch((error) => {
				console.warn('[Shadematch] BK2 product template failed', error);
			});

		return () => {
			isMounted = false;
		};
	}, [selectedShade?.skuId]);

	useEffect(() => {
		let isMounted = true;
		const beautyKit2 = new BeautyKit2();
		beautyKit2Ref.current = beautyKit2;

		beautyKit2
			.load()
			.then(() => beautyKit2.start(videoCanvasId, `#${renderingCanvasId}`, true))
			.then(() => {
				if (isMounted) {
					setIsReady(true);
					onReady();
				}
			})
			.catch((error) => {
				console.warn('[Shadematch] BK2 camera failed', error);
				if (isMounted) {
					setHasCameraError(true);
					onReady();
				}
			});

		return () => {
			isMounted = false;
			beautyKit2.stop();
			beautyKit2Ref.current = null;
		};
	}, [onReady, renderingCanvasId, videoCanvasId]);

	useEffect(() => {
		const beautyKit2 = beautyKit2Ref.current;
		if (!beautyKit2 || !isReady) {
			return;
		}

		if (!selectedShade) {
			beautyKit2.setLayers([]);
			return;
		}

		beautyKit2.setLayers([
			new Layer(
				getBK2ShapePath(foundationTemplate),
				getBK2Effect(foundationTemplate),
				getBK2ShapeType(foundationTemplate),
				hexToRgb(selectedShade.color),
				getBK2Opacity(foundationTemplate),
				foundationTemplate.beautifierLevel ?? 0,
				foundationTemplate.shineLevel ?? 0,
				selectedShade.name || foundationTemplate.name,
				selectedShade.skuId ?? foundationTemplate.sku,
			),
		]);
		onEnabled();
	}, [foundationTemplate, isReady, onEnabled, selectedShade]);

	return (
		<VTOFrame>
			<VTORenderingCanvas id={renderingCanvasId} />
			<VTORawViewport $visible={showBeforeAfter}>
				<VTORawCanvas id={videoCanvasId} width={beautyKit2Ref.current?.resolution ?? 720} height={beautyKit2Ref.current?.resolution ?? 720} $visible={showBeforeAfter} />
			</VTORawViewport>
			{!isReady && !hasCameraError && <VTOLoader>Loading</VTOLoader>}
			{hasCameraError && <VTOLoader>Camera unavailable</VTOLoader>}
		</VTOFrame>
	);
};

const ShadematchResults = () => {
	const { t } = useTranslation();
	const { imageUser: userImg, wristImageUser, market } = useSelector((state: CliniqueState) => state.session);
	const [apiProducts, setApiProducts] = useState<ShadematchProduct[]>([]);
	const [isProductLoading, setProductLoading] = useState(true);
	const [shadeRecommendation, setShadeRecommendation] = useState<ShadematchApiResponse | null>(null);
	const [isShadeRecommendationLoading, setShadeRecommendationLoading] = useState(true);
	const [selectedProductIndex, setSelectedProductIndex] = useState(0);
	const [selectedShadeIndexes, setSelectedShadeIndexes] = useState<number[]>([]);
	const [isBeforeAfterMode, setIsBeforeAfterMode] = useState(false);
	const [isVtoReady, setVtoReady] = useState(false);
	const photoAreaRef = useRef<HTMLDivElement>(null);
	const productCarouselRef = useRef<HTMLDivElement>(null);
	const shadeCarouselRef = useRef<HTMLDivElement>(null);
	const productCardRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const shadeRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const productScrollFrameRef = useRef<number | null>(null);
	const shadeScrollFrameRef = useRef<number | null>(null);
	const hasTrackedResultsViewedRef = useRef(false);
	const lastTrackedVtoShadeSkuRef = useRef<string | null>(null);
	const displayProducts = useMemo(() => applyShadeRecommendations(apiProducts, shadeRecommendation), [apiProducts, shadeRecommendation]);
	const selectedProduct = displayProducts[selectedProductIndex] ?? displayProducts[0] ?? null;
	const shades = selectedProduct?.shades ?? [];
	const recommendedShadeIndex = selectedProduct ? getRecommendedShadeIndex(selectedProduct) : 0;
	const selectedShadeIndex = selectedShadeIndexes[selectedProductIndex] ?? recommendedShadeIndex;
	const selectedShade = shades[selectedShadeIndex] ?? shades[recommendedShadeIndex] ?? null;
	const lighterLabel = t('lighter', 'Lighter');
	const matchLabel = t('match', 'Match');
	const darkerLabel = t('darker', 'Darker');
	const isResultLoading = isProductLoading || isShadeRecommendationLoading || !shadeRecommendation;
	const isLoadingOverlayDisplayed = isProductLoading || isShadeRecommendationLoading || !isVtoReady;
	const hasTrackedLoadingCompletedRef = useRef(false);

	const handleVtoReady = useCallback(() => {
		setVtoReady(true);
	}, []);

	useEffect(() => {
		if (isLoadingOverlayDisplayed || hasTrackedLoadingCompletedRef.current) return;

		hasTrackedLoadingCompletedRef.current = true;
		trackPlausibleEvent('sm_loading_completed');
	}, [isLoadingOverlayDisplayed]);

	useEffect(() => {
		if (isResultLoading || !shadeRecommendation || hasTrackedResultsViewedRef.current) return;

		hasTrackedResultsViewedRef.current = true;
		trackPlausibleEvent('sm_diagnosis_completed');
		trackPlausibleEvent('sm_internal_ita', {
			props: { ita: shadeRecommendation.ita },
		});
		trackPlausibleEvent('sm_internal_undertone', {
			props: {
				undertone: shadeRecommendation.undertone,
				confidence: shadeRecommendation.undertone_confidence,
			},
		});
		trackPlausibleEvent('sm_recommendation_viewed');
	}, [isResultLoading, shadeRecommendation]);

	const handleVtoEnabled = useCallback(() => {
		const productSku = selectedShade?.skuId;
		if (!productSku || lastTrackedVtoShadeSkuRef.current === productSku) {
			return;
		}

		lastTrackedVtoShadeSkuRef.current = productSku;
		trackPlausibleEvent('sm_product_vto_enabled', {
			props: {
				product_sku: productSku,
				product_base_id: selectedProduct?.id,
				shade_name: selectedShade.name,
			},
		});
	}, [selectedProduct?.id, selectedShade?.name, selectedShade?.skuId]);

	useEffect(() => {
		let isMounted = true;
		setProductLoading(true);

		getShadematchProducts(market)
			.then((products) => {
				if (!isMounted) {
					return;
				}

				setApiProducts(products);
			})
			.catch((error) => {
				console.error('[Shadematch] ELC products failed', error);
				if (isMounted) {
					setApiProducts([]);
				}
			})
			.finally(() => {
				if (isMounted) {
					setProductLoading(false);
				}
			});

		return () => {
			isMounted = false;
		};
	}, [market]);

	useEffect(() => {
		setShadeRecommendation(null);
		setShadeRecommendationLoading(true);

		if (!userImg || !wristImageUser) {
			return;
		}

		getShadeRecommendation(userImg, wristImageUser, market)
			.then((recommendation) => {
				setShadeRecommendation(recommendation);
			})
			.catch((error) => {
				console.error('[Shadematch] shade recommendation failed', error);
			})
			.finally(() => {
				setShadeRecommendationLoading(false);
			});
	}, [market, userImg, wristImageUser]);

	useEffect(() => {
		if (displayProducts.length === 0) {
			return;
		}

		setSelectedProductIndex(0);
		setSelectedShadeIndexes(displayProducts.map(getRecommendedShadeIndex));
	}, [displayProducts]);

	useEffect(() => {
		if (isResultLoading || !selectedProduct) {
			return;
		}

		const frame = requestAnimationFrame(() => {
			const shadeIndex = getRecommendedShadeIndex(selectedProduct);
			centerShade(shadeIndex, 'auto');
		});

		return () => cancelAnimationFrame(frame);
	}, [isResultLoading, selectedProduct, selectedProductIndex]);

	const centerShade = (index: number, behavior: ScrollBehavior = 'smooth') => {
		const carousel = shadeCarouselRef.current;
		const shade = shadeRefs.current[index];

		if (!carousel || !shade) {
			return;
		}

		carousel.scrollTo({
			left: shade.offsetLeft - carousel.clientWidth / 2 + shade.clientWidth / 2,
			behavior,
		});
	};

	const handleShadeSelect = (index: number) => {
		setSelectedShadeIndexes((currentIndexes) => currentIndexes.map((currentIndex, productIndex) => (productIndex === selectedProductIndex ? index : currentIndex)));
		centerShade(index);
	};
	const handleProductSelect = (index: number) => {
		setSelectedProductIndex(index);
		productCardRefs.current[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
	};
	const updateSelectedProductFromScroll = () => {
		const carousel = productCarouselRef.current;
		if (!carousel) return;

		const carouselCenter = carousel.getBoundingClientRect().left + carousel.clientWidth / 2;
		let nearestIndex = selectedProductIndex;
		let nearestDistance = Infinity;

		productCardRefs.current.forEach((card, index) => {
			if (!card) return;

			const cardRect = card.getBoundingClientRect();
			const cardCenter = cardRect.left + cardRect.width / 2;
			const distance = Math.abs(cardCenter - carouselCenter);
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearestIndex = index;
			}
		});

		setSelectedProductIndex((currentIndex) => (currentIndex === nearestIndex ? currentIndex : nearestIndex));
	};
	const updateSelectedShadeFromScroll = () => {
		const carousel = shadeCarouselRef.current;
		if (!carousel) return;

		const carouselCenter = carousel.getBoundingClientRect().left + carousel.clientWidth / 2;
		let nearestIndex = selectedShadeIndex;
		let nearestDistance = Infinity;

		shadeRefs.current.forEach((shade, index) => {
			if (!shade) return;

			const shadeRect = shade.getBoundingClientRect();
			const shadeCenter = shadeRect.left + shadeRect.width / 2;
			const distance = Math.abs(shadeCenter - carouselCenter);
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearestIndex = index;
			}
		});

		setSelectedShadeIndexes((currentIndexes) => currentIndexes.map((currentIndex, productIndex) => (productIndex === selectedProductIndex && currentIndex !== nearestIndex ? nearestIndex : currentIndex)));
	};
	const handleProductScroll = () => {
		if (productScrollFrameRef.current !== null) {
			cancelAnimationFrame(productScrollFrameRef.current);
		}

		productScrollFrameRef.current = requestAnimationFrame(() => {
			updateSelectedProductFromScroll();
			productScrollFrameRef.current = null;
		});
	};
	const handleShadeScroll = () => {
		if (shadeScrollFrameRef.current !== null) {
			cancelAnimationFrame(shadeScrollFrameRef.current);
		}

		shadeScrollFrameRef.current = requestAnimationFrame(() => {
			updateSelectedShadeFromScroll();
			shadeScrollFrameRef.current = null;
		});
	};
	const updateBeforeAfterPosition = (clientX: number, element: HTMLElement) => {
		const rect = element.getBoundingClientRect();
		const nextPosition = ((clientX - rect.left) / rect.width) * 100;
		element.style.setProperty('--before-after-position', `${Math.min(100, Math.max(0, nextPosition))}%`);
	};
	const handleDividerPointerDown = (event: PointerEvent<HTMLDivElement>) => {
		event.currentTarget.setPointerCapture(event.pointerId);
		updateBeforeAfterPosition(event.clientX, event.currentTarget.parentElement!);
	};
	const handleDividerPointerMove = (event: PointerEvent<HTMLDivElement>) => {
		if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;

		updateBeforeAfterPosition(event.clientX, event.currentTarget.parentElement!);
	};
	return (
		<Component initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
			<PhotoArea ref={photoAreaRef}>
				<ShadematchVirtualTryOn selectedShade={isResultLoading ? null : selectedShade} showBeforeAfter={isBeforeAfterMode && !isResultLoading && Boolean(selectedProduct)} onEnabled={handleVtoEnabled} onReady={handleVtoReady} />

				{isBeforeAfterMode && !isResultLoading && selectedProduct && (
					<>
						<BeforeLabel>Before</BeforeLabel>
						<AfterLabel>After</AfterLabel>
						<Divider onPointerDown={handleDividerPointerDown} onPointerMove={handleDividerPointerMove}>
							<Handle>
								<HandleLine />
								<HandleLine />
								<HandleLine />
							</Handle>
						</Divider>
					</>
				)}
			</PhotoArea>

			{!isResultLoading && selectedProduct && (
				<BeforeAfterButton type='button' onClick={() => setIsBeforeAfterMode((isActive) => !isActive)} aria-label='Toggle before after'>
					<BeforeAfterIcon src={isBeforeAfterMode ? unionIcon : squareIcon} />
				</BeforeAfterButton>
			)}

			{!isResultLoading && displayProducts.length > 0 && (
				<ProductCarousel ref={productCarouselRef} aria-label='Products' onScroll={handleProductScroll}>
					{displayProducts.map((product, index) => (
						<ProductCard
							key={product.id}
							ref={(element) => {
								productCardRefs.current[index] = element;
							}}
							$selected={index === selectedProductIndex}
							onClick={() => handleProductSelect(index)}>
							<ProductName>{product.name}</ProductName>
							{product.coverageKey && product.finishKey && <ProductDetail>{t(product.coverageKey)} • {t(product.finishKey)}</ProductDetail>}
						</ProductCard>
					))}
				</ProductCarousel>
			)}

			<BottomPanel>
				{isResultLoading ? (
					<ShadeSkeletonContainer aria-label='Loading shades'>
						{Array.from({ length: 7 }).map((_, index) => (
							<ShadeSkeletonItem key={index}>
								<ShadeSkeletonSquare />
								<ShadeSkeletonText />
							</ShadeSkeletonItem>
						))}
					</ShadeSkeletonContainer>
				) : selectedProduct ? (
					<ShadeCarousel ref={shadeCarouselRef} aria-label='Shades' onScroll={handleShadeScroll}>
						<ShadeStrip>
							<FeaturedFrame $matchIndex={recommendedShadeIndex}>
								<ShadeRole $fontSize={getShadeRoleFontSize(lighterLabel)}>{lighterLabel}</ShadeRole>
								<ShadeRole $selected $fontSize={getShadeRoleFontSize(matchLabel)}>{matchLabel}</ShadeRole>
								<ShadeRole $fontSize={getShadeRoleFontSize(darkerLabel)}>{darkerLabel}</ShadeRole>
							</FeaturedFrame>
							{shades.map((shade, index) => (
								<ShadeOption
									key={`${shade.name}-${index}`}
									ref={(element) => {
										shadeRefs.current[index] = element;
									}}
									onClick={() => handleShadeSelect(index)}>
									<ShadeSquare $color={shade.color} $selected={index === selectedShadeIndex} />
									<ShadeName $selected={index === selectedShadeIndex}>{shade.name}</ShadeName>
								</ShadeOption>
							))}
						</ShadeStrip>
					</ShadeCarousel>
				) : null}
			</BottomPanel>

			<AnimatePresence>{isLoadingOverlayDisplayed && <ShadematchLoadingOverlay />}</AnimatePresence>
		</Component>
	);
};

const Component = styled(motion.div)`
	--top-offset: 48px;
	--shade-panel-height: 116px;
	--product-carousel-height: 116px;
	--photo-bottom: var(--shade-panel-height);
	min-height: 100vh;
	background: #fff;
	color: #111;
	overflow: hidden;
	padding-top: var(--top-offset);
	box-sizing: border-box;

	@media (max-height: 700px) {
		--shade-panel-height: 112px;
		--product-carousel-height: 108px;
	}
`;

const PhotoArea = styled.div`
	position: fixed;
	top: var(--top-offset);
	left: 0;
	right: 0;
	bottom: var(--photo-bottom);
	overflow: hidden;
	background: #f3f3f3;
	--before-after-position: 50%;
`;

const VTOFrame = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
`;

const VTORenderingCanvas = styled.canvas`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
	background: #f3f3f3;
	transform: scaleX(-1) scale(1.6);
	transform-origin: center center;
`;

const VTORawViewport = styled.div<{ $visible: boolean }>`
	position: absolute;
	inset: 0;
	z-index: 2;
	overflow: hidden;
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	pointer-events: none;
	clip-path: inset(0 calc(100% - var(--before-after-position)) 0 0);
`;

const VTORawCanvas = styled.canvas<{ $visible: boolean }>`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
	background: #f3f3f3;
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	pointer-events: none;
	transform: scaleX(-1) scale(1.6);
	transform-origin: center center;
`;

const VTOLoader = styled.div`
	position: absolute;
	inset: 0;
	z-index: 4;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f3f3f3;
	color: #111;
	font: normal normal 700 16px/100% 'Clinique Helvetica Neue';
`;

const BeforeLabel = styled.p`
	position: absolute;
	top: 18px;
	left: 26px;
	z-index: 3;
	margin: 0;
	color: white;
	font: normal normal 700 24px/100% 'Clinique Helvetica Neue';
	text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
	pointer-events: none;
`;

const AfterLabel = styled(BeforeLabel)`
	left: auto;
	right: 26px;
`;

const Divider = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: var(--before-after-position);
	z-index: 3;
	width: 2px;
	transform: translateX(-50%);
	background: rgba(255, 255, 255, 0.8);
	display: flex;
	align-items: center;
	justify-content: center;
	touch-action: none;
	cursor: ew-resize;
`;

const Handle = styled.div`
	width: 52px;
	min-width: 52px;
	height: 52px;
	flex: 0 0 52px;
	border-radius: 50%;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
`;

const HandleLine = styled.span`
	width: 2px;
	height: 24px;
	background: #d9d9d9;
`;

const BeforeAfterButton = styled.button`
	position: fixed;
	right: 20px;
	bottom: calc(var(--shade-panel-height) + var(--product-carousel-height) + 16px);
	z-index: 4;
	width: 48px;
	height: 48px;
	border: 0;
	border-radius: 50%;
	background: #000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	box-shadow: 0 4px 14px rgba(0, 0, 0, 0.24);
`;

const BeforeAfterIcon = styled.img`
	width: 22px;
	height: 22px;
	object-fit: contain;
	display: block;
`;

const ProductCarousel = styled.div`
	position: fixed;
	left: 0;
	right: 0;
	bottom: var(--shade-panel-height);
	z-index: 2;
	display: flex;
	gap: 18px;
	overflow-x: auto;
	padding: 0 calc((100vw - min(82vw, 320px)) / 2) 16px;
	scroll-snap-type: x mandatory;
	scroll-padding-inline: calc((100vw - min(82vw, 320px)) / 2);
	overscroll-behavior-x: contain;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const ProductCard = styled.button<{ $selected: boolean }>`
	appearance: none;
	border: 1px solid ${(props) => (props.$selected ? 'black' : '#dedede')};
	background: ${(props) => (props.$selected ? 'black' : 'rgba(255, 255, 255, 0.75)')};
	color: ${(props) => (props.$selected ? 'white' : '#111')};
	border-radius: 6px;
	width: min(82vw, 320px);
	min-width: min(82vw, 320px);
	min-height: 100px;
	padding: 8px;
	text-align: left;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
	scroll-snap-align: center;
	scroll-snap-stop: always;
	transition: background 0.24s ease, color 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease;
`;

const ProductName = styled.p`
	margin: 0 0 8px;
	font: normal normal 700 16px/135% 'Clinique Helvetica Neue';
`;

const ProductDetail = styled.p`
	margin: 0;
	color: currentColor;
	opacity: 0.66;
	font: normal normal 400 16px/135% 'Clinique Helvetica Neue';
`;

const BottomPanel = styled.div`
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 3;
	background: white;
	height: var(--shade-panel-height);
	padding: 0 20px 8px;
	box-sizing: border-box;
`;

const ShadeCarousel = styled.div`
	position: relative;
	margin: 0 -20px;
	height: 108px;
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior-x: contain;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const shine = keyframes`
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
`;

const skeletonBackground = `
	background: #eee;
	background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
	background-size: 200% 100%;
`;

const ShadeSkeletonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 12px;
	padding: 34px 20px 20px;
	height: 108px;
	box-sizing: border-box;
	overflow: hidden;
`;

const ShadeSkeletonItem = styled.div`
	width: 44px;
	min-width: 44px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

const ShadeSkeletonSquare = styled.div`
	width: 44px;
	height: 44px;
	${skeletonBackground}
	animation: 1.5s ${shine} linear infinite;
`;

const ShadeSkeletonText = styled.div`
	width: 36px;
	height: 10px;
	border-radius: 999px;
	${skeletonBackground}
	animation: 1.5s ${shine} linear infinite;
`;

const ShadeStrip = styled.div`
	--shade-side-padding: max(20px, calc((100vw - 172px) / 2));
	display: flex;
	gap: 12px;
	width: max-content;
	min-width: 100%;
	padding: 34px var(--shade-side-padding) 20px;
	position: relative;
`;

const ShadeOption = styled.button`
	appearance: none;
	border: 0;
	background: transparent;
	padding: 2px 0;
	width: 44px;
	min-width: 44px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	position: relative;
	z-index: 2;
`;

const ShadeRole = styled.span<{ $selected?: boolean; $fontSize: number }>`
	width: 56px;
	min-height: 24px;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	font: normal normal ${(props) => (props.$selected ? 700 : 400)} ${(props) => props.$fontSize}px/105% 'Clinique Helvetica Neue';
	white-space: normal;
	text-align: center;
	overflow-wrap: anywhere;
	color: black;
`;

const ShadeSquare = styled.div<{ $color: string; $selected: boolean }>`
	width: 44px;
	height: 44px;
	aspect-ratio: 1 / 1;
	background: ${(props) => props.$color};
	border: ${(props) => (props.$selected ? '2px solid #111' : '1px solid transparent')};
	box-sizing: border-box;
`;

const ShadeName = styled.span<{ $selected: boolean }>`
	font: normal normal ${(props) => (props.$selected ? 700 : 400)} 12px/100% 'Clinique Helvetica Neue';
	white-space: nowrap;
	color: black;
`;

const FeaturedFrame = styled.div<{ $matchIndex: number }>`
	pointer-events: none;
	position: absolute;
	left: ${(props) => `calc(var(--shade-side-padding) + ${(props.$matchIndex - 1) * 56 - 8}px)`};
	top: 4px;
	width: 172px;
	height: 100px;
	border: 2px solid #e6e6e6;
	border-radius: 8px;
	background: transparent;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
	box-sizing: border-box;
	z-index: 1;
	display: grid;
	grid-template-columns: repeat(3, 56px);
	align-items: start;
	justify-items: center;
	justify-content: center;
	padding: 6px 0;
`;

export default ShadematchResults;
